import * as R from 'ramda';
import React from 'react';
import * as Fields from './fields';
// XXX This is causing misfunction of babel plugins

// Thin wrapper around form to deal with normalization (through Field), rendering, data collecting & tranform.
// Not support complex structure such as nesting.
// TODO
// - Form to accept field instance as well
// - Field can be hidden if there is only one choice

export class FormWrapper {
  constructor(form, initialData, fields) {
    this.form = form;
    this.initialData = { ...initialData };
    this.fields = fields.map((field) => this.getField(field));
  }

  getField(spec) {
    if (spec instanceof Fields.BaseField) return spec;
    if (spec.render) return spec;
    // TODO initialData will be overrode by the one from spec, is it correct? Any better logic?
    const initialData = this.initialData[spec.name];
    const Field = Fields[`${spec.type}Field`];
    return new Field({
      ...spec,
      initialValue: R.isNil(initialData) ? spec.initialValue : initialData,
    });
  }

  getFields() {
    return this.fields.map((field) => field);
  }

  getFieldNames() {
    return this.fields.map((field) => field.spec?.name || field.name);
  }

  hasField(name) {
    return this.fields.find(
      (field) => field?.spec?.name === name || field?.name === name
    );
  }

  renderField(name) {
    return this.hasField(name)?.render(this);
  }

  render() {
    return this.fields.map((field, index) => {
      const element = field.render(this);
      // If element already has a key, use it; otherwise add index-based key
      if (React.isValidElement(element) && !element.key) {
        return React.cloneElement(element, { key: `field-${index}` });
      }
      return element;
    });
  }

  serialize() {
    const values = this.form.getFieldsValue();
    return R.mergeAll(
      this.fields
        .filter((field) => field.serializeField)
        .map((field) => field.serializeField(values))
    );
  }

  // This method call error field's name based on the field's label, if there is no field's label, then call it 'Field'
  setErrors(errors, values = this.form.getFieldsValue()) {
    if (!R.is(Object, errors)) return;

    // Map server field names to form field names
    const errorKeyMapping = {
      toetd: 'etd',
      frometd: 'etd',
    };

    // Remap error keys to match form field names, keeping both original and mapped fields
    const remappedErrors = { ...errors };
    Object.entries(errorKeyMapping).forEach(([serverKey, formKey]) => {
      if (remappedErrors[serverKey]) {
        remappedErrors[formKey] = remappedErrors[serverKey];
        // Keep the original server key as well
      }
    });

    this.form.setFields(
      R.mergeAll(
        R.toPairs(remappedErrors).map(([key, es]) => ({
          [key]: {
            value: values[key],
            errors:
              es.map != null &&
              es.map(
                (e) =>
                  new Error(
                    e.replace(/\{(\w+)\}/g, (m, name) =>
                      R.compose(
                        R.pathOr('Field', ['spec', 'label']),
                        R.find(R.pathEq(['spec', 'name'], name))
                      )(this.fields)
                    )
                  )
              ),
          },
        }))
      )
    );

    // HACK DOM to display errors for variants fields due to unable to set errors to nested forms in Ant Design v3
    // Clear all existing variant errors first to prevent stale errors
    this.clearAllVariantErrors();
    const variantsErrors = this.extractVariantsErrors(errors);
    if (Object.keys(variantsErrors).length > 0) {
      this.setVariantsErrors(variantsErrors);
    }

    if (errors.files) {
      // HACK DOM to display errors for file fields due to unable to set errors to the custom file upload widget
      const fileDom = document.querySelector(
        '.upload-attachments .nui-fieldset'
      );

      fileDom?.querySelector('.error-list')?.remove();

      const errorNode = document.createElement('ul');
      errorNode.classList.add('error-list');

      const errorItem = document.createElement('li');
      errorItem.classList.add('error-description');
      errorItem.textContent = errors.files[0];

      errorNode.appendChild(errorItem);

      fileDom?.classList.add('has-error');
      fileDom?.appendChild(errorNode);
    }
  }

  extractVariantsErrors(errors) {
    const variantsErrors = {};

    // Handle nested structure: errors.variants["variants-{id}"][0].{field}
    if (errors.variants && typeof errors.variants === 'object') {
      Object.entries(errors.variants).forEach(([variantKey, variantArray]) => {
        // Match any characters for ID (including hyphens, UUIDs, etc.)
        const match = variantKey.match(/^variants-(.+)$/);
        if (match && Array.isArray(variantArray) && variantArray.length > 0) {
          const [, id] = match;
          const variantErrors = variantArray[0];
          if (variantErrors && typeof variantErrors === 'object') {
            if (!variantsErrors[id]) variantsErrors[id] = {};
            Object.entries(variantErrors).forEach(([field, errorMessages]) => {
              if (Array.isArray(errorMessages) && errorMessages.length > 0) {
                variantsErrors[id][field] = errorMessages[0];
              }
            });
          }
        }
      });
    }

    // Also handle flat structure: errors["variants-{id}_{field}"]
    Object.keys(errors).forEach((key) => {
      // Match any characters for both ID and field (including hyphens, special chars, etc.)
      const match = key.match(/^variants-(.+?)_(.+)$/);
      if (match) {
        const [, id, field] = match;
        if (!variantsErrors[id]) variantsErrors[id] = {};
        variantsErrors[id][field] = Array.isArray(errors[key])
          ? errors[key][0]
          : errors[key];
      }
    });

    return variantsErrors;
  }

  clearAllVariantErrors() {
    // Clear all variant errors from all .variants-item elements
    document.querySelectorAll('.variants-item').forEach((variantItemDom) => {
      this.clearVariantErrors(variantItemDom);
    });
  }

  setVariantsErrors(variantsErrors) {
    Object.entries(variantsErrors).forEach(([id, fieldErrors]) => {
      const variantItemDom = document.querySelector(
        `.variants-item[data-id="${id}"]`
      );
      if (!variantItemDom) return;

      // Add new errors
      Object.entries(fieldErrors).forEach(([field, errorMsg]) => {
        this.addFieldError(variantItemDom, field, errorMsg);
      });
    });
  }

  clearVariantErrors(containerDom) {
    containerDom.querySelectorAll('.has-error').forEach((node) => {
      node.classList.remove('has-error');
    });
    containerDom.querySelectorAll('.ant-form-explain').forEach((node) => {
      node.remove();
    });
  }

  addFieldError(containerDom, fieldClass, errorMessage) {
    const fieldDom = containerDom.querySelector(
      `.${fieldClass} .ant-form-item-control`
    );
    if (!fieldDom) return;

    const errorNode = document.createElement('div');
    errorNode.classList.add('ant-form-explain');
    errorNode.textContent = errorMessage;

    fieldDom.classList.add('has-error');
    fieldDom.appendChild(errorNode);
  }
}
